import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";
import { IMAGES, PRACTICES } from "../../data/marketing";

const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];
const images = [IMAGES.epr, IMAGES.solid, IMAGES.bwg, IMAGES.circular, IMAGES.csr];
const captions = ["Illustrative recovery line", "Illustrative processing facility", "Illustrative on-site segregation", "Illustrative circular material", "Illustrative community logistics"];

export default function BentoPractices() {
  return (
    <section id="practices" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <div className="max-w-3xl">
        <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">01 · Our practices</div>
        <h2 className="mt-3 font-display text-3xl font-medium tracking-tight text-forest-deep sm:text-4xl">Expertise across the waste value chain.</h2>
        <p className="mt-4 text-base leading-relaxed text-forest-deep/65 sm:text-lg">Start with a regulatory obligation, an operational challenge or a project ambition. We define the right scope and connect the expertise your mandate needs.</p>
      </div>
      <div className="mt-12 grid gap-5 lg:grid-cols-12">
        {PRACTICES.map((practice, index) => (
          <Reveal key={practice.slug} delay={index * 0.05} className={spans[index]}>
            <Link to={`/practice/${practice.slug}`} className="group relative flex h-full min-h-[360px] flex-col overflow-hidden rounded-[2rem] bg-forest-deep text-[#FBF9F4] shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(5,31,20,0.18)]" data-testid={`practice-card-${practice.slug}`}>
              <motion.img src={images[index]} alt={captions[index]} className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105 group-hover:opacity-55" loading={index > 1 ? "lazy" : "eager"} />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/55 to-forest-deep/10" />
              <span className="absolute right-5 top-5 rounded-full bg-black/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/80 backdrop-blur">{captions[index]}</span>
              <div className="relative mt-auto p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono text-xs font-semibold text-emerald-300/80">{practice.num}</span>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur transition-transform group-hover:translate-x-1 group-hover:-translate-y-1">
                    <ArrowUpRight className="h-5 w-5 text-emerald-300" />
                  </span>
                </div>
                <p className="mt-8 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">{practice.audience}</p>
                <h3 className="mt-3 font-display text-3xl font-medium leading-tight">{practice.title}</h3>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-emerald-100/70">{practice.summary}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {practice.points.slice(0, 3).map((point) => <span key={point} className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-emerald-100/80 backdrop-blur">{point}</span>)}
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <div className="mt-8 rounded-2xl bg-forest-deep/[0.04] px-5 py-4 text-sm font-semibold text-forest-deep/70" data-testid="practice-universal-layer">
        Across every practice: Data and baselines · Service provider diligence · Traceability · Reporting and evidence
      </div>
    </section>
  );
}
